import { CheckCircle2, Lock } from 'lucide-react';
import { Button } from '@fq/ui/components/ui/button';
import { cn } from './utils';

type Props = {
  picksMade: number;
  totalPicks: number;
  isSubmitted: boolean;
  onSubmit: () => void;
  /** Hide entirely (e.g. signed out, or event is past lock time). */
  hidden?: boolean;
  /** Visual layout. `inline` renders a normal in-flow button. `sticky`
   * renders a full-width bottom bar fixed to the viewport — use on mobile. */
  layout: 'inline' | 'sticky';
  className?: string;
};

export function SubmitPicksButton({
  picksMade,
  totalPicks,
  isSubmitted,
  onSubmit,
  hidden,
  layout,
  className,
}: Props) {
  if (hidden) return null;

  const remaining = Math.max(0, totalPicks - picksMade);
  const allDone = totalPicks > 0 && remaining === 0;
  const disabled = !allDone;

  const label = isSubmitted
    ? 'Submitted — view again'
    : !allDone
      ? `Pick ${remaining} more match${remaining === 1 ? '' : 'es'} to submit`
      : 'Submit picks';

  const Icon = isSubmitted ? CheckCircle2 : Lock;

  if (layout === 'sticky') {
    return (
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-3',
          className,
        )}
      >
        <Button
          size="lg"
          className={cn(
            'w-full h-12',
            isSubmitted &&
              'bg-amber-500 hover:bg-amber-500/90 text-amber-950',
          )}
          onClick={onSubmit}
          disabled={disabled && !isSubmitted}
        >
          <Icon className="h-4 w-4 mr-2" />
          {label}
        </Button>
      </div>
    );
  }

  const helperText = isSubmitted
    ? 'You can keep editing — picks save as you go'
    : allDone
      ? 'Ready to lock in your bracket'
      : null;

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-3 flex-wrap',
        className,
      )}
    >
      {helperText && (
        <p className="text-sm text-muted-foreground">{helperText}</p>
      )}
      <Button
        className={cn(
          isSubmitted &&
            'bg-amber-500 hover:bg-amber-500/90 text-amber-950 dark:text-amber-950',
        )}
        onClick={onSubmit}
        disabled={disabled && !isSubmitted}
      >
        <Icon className="h-4 w-4 mr-2" />
        {label}
      </Button>
    </div>
  );
}
